import React, { useEffect, useMemo, useRef, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import investorApi from '/src/apis/investorApi'
import HorizontalBarChart from '/src/components/Reusable/Charts/HorizontalBarChart'
import { formatAmount } from '/src/lib/helpers'
import { buildRows } from '../utils'
import { COMPOSITION_TABS, COMPOSITION_NAME_KEYS } from '../constants'

const formatCompositionAmount = amount => amount === 0 ? 'Undisclosed' : formatAmount(amount)

const toChartData = (rows, tabId, width) => {
    const total = rows.reduce((s, r) => s + r.amount, 0)
    return {
        color_table: rows.map(r => r.color),
        dataset: rows.map(r => [r.name, r.amount]),
        x_domain: [0, total],
        id: `portfolio-composition-${tabId}`,
        value_formatter: formatCompositionAmount,
        tooltip_data: rows.map(r => [r.name, formatCompositionAmount(r.amount)]),
        tooltip_keys: ["Name", "Capital"],
        height: rows.length * 36 + 24,
        width: width || 600,
        value_param: "amount",
        show_xAxis: false,
        margin_left: 200,
        margin_right: rows.some(r => r.amount === 0) ? 85 : 60,
        margin_bottom: 4,
        track_color: '#f5f5f5',
        cornered: true,
        show_left_line: false,
        label_inside: true,
        label_colors: rows.map(r => r.isOther ? '#5d656d' : '#fff'),
        pct_data: rows.map(r => r.pctLabel),
        left_align_y_labels: true,
        y_padding: 0.35,
    }
}

const FETCHERS = {
    geo: (id, type, fundFilters) => investorApi.getInvestorGeographyAggregations(id, type, fundFilters),
    industry: (id, type, fundFilters) => investorApi.getInvestorIndustryAggregations(id, type, fundFilters),
    stage: (id, type, fundFilters) => investorApi.getInvestorStageAggregations(id, type, fundFilters),
}

const NO_FILTERS = []

export default function PortfolioComposition({ entityId, entityType, fundFilters = NO_FILTERS }) {
    const [activeTab, setActiveTab] = useState('geo')
    const [compData,  setCompData]  = useState({ geo: null, industry: null, stage: null })
    const [width,     setWidth]     = useState(null)
    const fetchingTabs = useRef(new Set())
    // Bumped when the fund filter changes so in-flight responses for the old filter are discarded
    const filtersVersion = useRef(0)
    const wrapRef      = useRef(null)

    useEffect(() => {
        const el = wrapRef.current
        if (!el || typeof ResizeObserver === 'undefined') return
        let timer = null
        const ro = new ResizeObserver(() => {
            clearTimeout(timer)
            timer = setTimeout(() => {
                const w = el.offsetWidth
                if (w > 100) setWidth(w)
            }, 100)
        })
        ro.observe(el)
        return () => { ro.disconnect(); clearTimeout(timer) }
    }, [])

    const prevFilters = useRef(fundFilters)

    useEffect(() => {
        // Fund filter changed: drop the per-tab cache and refetch for the new filter
        let cache = compData
        if (prevFilters.current !== fundFilters) {
            prevFilters.current = fundFilters
            filtersVersion.current += 1
            fetchingTabs.current = new Set()
            cache = { geo: null, industry: null, stage: null }
            setCompData(cache)
        }

        if (cache[activeTab] !== null) return
        if (fetchingTabs.current.has(activeTab)) return

        fetchingTabs.current.add(activeTab)
        const version = filtersVersion.current
        FETCHERS[activeTab](entityId, entityType, fundFilters).then(res => {
            if (version !== filtersVersion.current) return
            fetchingTabs.current.delete(activeTab)
            const data = res?.data?.data ?? []
            setCompData(prev => ({ ...prev, [activeTab]: buildRows(data, COMPOSITION_NAME_KEYS[activeTab]) }))
        }).catch(() => {
            if (version !== filtersVersion.current) return
            fetchingTabs.current.delete(activeTab)
            setCompData(prev => ({ ...prev, [activeTab]: [] }))
        })
    }, [activeTab, entityId, entityType, fundFilters])

    const rows      = compData[activeTab]
    const chartData = useMemo(
        () => rows && rows.length > 0 && width ? toChartData(rows, activeTab, width) : null,
        [rows, activeTab, width]
    )

    return (
        <div ref={wrapRef} className="pi-card">
            <div className="pi-card__title">Portfolio composition</div>
            <div className="pi-card__subtitle">Capital allocation breakdown</div>

            <div className="pi-tabs">
                {COMPOSITION_TABS.map(t => (
                    <button
                        key={t.id}
                        onClick={() => setActiveTab(t.id)}
                        className={`pi-tabs__btn${activeTab === t.id ? ' pi-tabs__btn--active' : ''}`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {rows === null ? (
                <div style={{ flex: 1 }}>
                    {[1, 2, 3, 4].map(n => (
                        <div key={n} style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                            <Skeleton width={110} height={12} style={{ marginRight: 10, flexShrink: 0 }} />
                            <Skeleton height={24} style={{ flex: 1 }} borderRadius={3} />
                        </div>
                    ))}
                </div>
            ) : rows.length === 0 ? (
                <div className="pi-empty pi-empty--no-data" style={{ flex: 1 }}>No data available</div>
            ) : (
                <HorizontalBarChart data={chartData} />
            )}
        </div>
    )
}
